"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { AnimatePresence, m } from "framer-motion"
import { ClientOnly } from "@/lib/animation/client-only"
import { stepsFor, type StepId } from "@/lib/quiz/questions"
import type { Answers } from "@/lib/quiz/types"
import type { InsurersData } from "@/lib/quiz/insurers"
import type { PriceMap } from "@/lib/pricing"
import { ProductInfoButton } from "@/components/ui/product-info-modal"
import { stepInfo } from "@/lib/quiz/product-info-map"
import { ProgressIndicator } from "./progress-indicator"
import { BackButton } from "./back-button"
import { useQuizProgress } from "./progress-context"
import { AgeStep } from "./steps/age-step"
import { GenderStep } from "./steps/gender-step"
import { InterestStep } from "./steps/interest-step"
import { MriStep } from "./steps/mri-step"
import { MriAddonsStep } from "./steps/mri-addons-step"
import { InsuranceStep } from "./steps/insurance-step"
import { EmailStep } from "./steps/email-step"
import { ResultPage } from "./result/result-page"
import { track } from "@/lib/analytics"
import { sendQuizCompletion } from "@/lib/inngest"
import { pushDataLayer } from "@/lib/gtm"
import { buildCompletionPayload } from "@/lib/quiz/completion-event"
import { useCountry } from "@/components/layout/country-provider"
import type { QuizMarket } from "@/lib/quiz/market"
import type { ReviewerExpert } from "@/lib/reviewer-experts"

type InterestKey =
  | "hormone_interest"
  | "omega_interest"
  | "b_vitamins_interest"
  | "trend_interest"

const stepToInterestKey: Partial<Record<StepId, InterestKey>> = {
  hormone: "hormone_interest",
  omega: "omega_interest",
  b_vitamins: "b_vitamins_interest",
  trend: "trend_interest",
}

interface WizardProps {
  prices: PriceMap
  productIds: PriceMap
  /** Insurer + supplementary-plan catalogue, fetched from the CMS server-side. */
  insurers: InsurersData
  experts: ReviewerExpert[]
  /** What this market can actually offer — resolved server-side. */
  market: QuizMarket
  /**
   * "default" — embedded page mode: page-level header, left-aligned step
   * questions, full progress bar (current /quiz).
   *
   * "focus" — Hims-style focused mode: no page-level header, centered
   * step questions with a tiny "Step X of Y" kicker, generous whitespace.
   * Used by /quiz-focus inside the minimal-chrome layout.
   */
  variant?: "default" | "focus"
}

// Sticky-nav offset so the scrolled-into-view question doesn't sit half behind
// the floating navbar pill. Approx height of `.mega-pill` + breathing room.
const HEADER_OFFSET_PX = 96

export function Wizard({
  prices,
  productIds,
  insurers,
  experts,
  market,
  variant = "default",
}: WizardProps) {
  const isFocus = variant === "focus"
  const t = useTranslations("quiz")
  const locale = useLocale()
  const country = useCountry()
  const [answers, setAnswers] = useState<Answers>({})
  const [index, setIndex] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const steps = useMemo(() => stepsFor(answers, market), [answers, market])
  const total = steps.length
  const current: StepId | "result" = index >= total ? "result" : steps[index]

  // Report progress to the chrome-level context when present (focused
  // mode). No-op for the embedded /quiz page, where no provider is
  // mounted and the hook returns null.
  const chromeProgress = useQuizProgress()
  useEffect(() => {
    chromeProgress?.setProgress({
      current: Math.min(index + 1, total),
      total,
    })
  }, [chromeProgress, index, total])

  // ── Funnel instrumentation (AHD-8497) ──
  // quiz_step_viewed fires for every step the user lands on, including the
  // first on mount; the result view is excluded. quiz_abandoned fires at most
  // once, carrying the step the user left from — via pagehide (tab close,
  // hard nav) or wizard unmount (in-site SPA nav). Reaching the result
  // permanently disarms it: a completion must never also count as an abandon.
  const lastViewedStepRef = useRef<StepId | null>(null)
  const abandonStepRef = useRef<{ stepId: StepId; stepIndex: number } | null>(
    null
  )
  const abandonFiredRef = useRef(false)
  useEffect(() => {
    if (current === "result") {
      abandonStepRef.current = null
      return
    }
    abandonStepRef.current = { stepId: current, stepIndex: index }
    // Dedupes the StrictMode double effect-run on the same step; a revisit
    // after going back is a genuine new view and fires again.
    if (lastViewedStepRef.current !== current) {
      lastViewedStepRef.current = current
      track.quizStepViewed(current, index, total)
    }
  }, [current, index, total])

  const pendingUnmountAbandonRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  useEffect(() => {
    // A StrictMode remount re-runs this effect right after its cleanup —
    // cancel the deferred unmount-abandon so the dev double-mount never
    // emits a false quiz_abandoned.
    if (pendingUnmountAbandonRef.current !== null) {
      clearTimeout(pendingUnmountAbandonRef.current)
      pendingUnmountAbandonRef.current = null
    }
    const fireAbandoned = () => {
      const step = abandonStepRef.current
      if (!step || abandonFiredRef.current) return
      abandonFiredRef.current = true
      track.quizAbandoned(step.stepId, step.stepIndex)
    }
    window.addEventListener("pagehide", fireAbandoned)
    return () => {
      window.removeEventListener("pagehide", fireAbandoned)
      // SPA nav away unmounts the wizard without a pagehide; defer so a
      // StrictMode remount can cancel above while a real unmount fires.
      pendingUnmountAbandonRef.current = setTimeout(fireAbandoned, 0)
    }
  }, [])

  // Scroll the wizard top into view whenever the active step changes — without
  // this, each transition lands the question half-behind the sticky navbar
  // because the page never re-anchors after the previous click. Skips the
  // initial render so the page doesn't auto-scroll on first paint.
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const el = wrapperRef.current
    if (!el) return
    const top =
      el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET_PX
    window.scrollTo({ top, behavior: "smooth" })
  }, [index])

  const advance = (patch: Partial<Answers>) => {
    setAnswers((a) => ({ ...a, ...patch }))
    const nextIndex = index + 1
    setIndex(nextIndex)
    track.quizStepAnswered(
      typeof current === "string" ? current : "result",
      patch,
      index
    )
  }

  const completeQuiz = (email: string, marketing_opt_in: boolean) => {
    const payload = buildCompletionPayload({
      answers,
      prices,
      productIds,
      insurers,
      locale,
      email,
      marketingConsent: marketing_opt_in,
      search: typeof window !== "undefined" ? window.location.search : "",
      country: country.code,
      market,
    })
    track.quizCompleted({
      recommendation_base: payload.base_tier,
      recommendation_addons: payload.add_ons.map((a) => a.id),
      age: answers.age,
      gender: answers.gender,
      insurer_id: answers.insurer_id,
      total_price_chf: payload.total_price_chf,
    })
    pushDataLayer({
      event: "quiz_completed",
      recommendation_base: payload.base_tier,
      value: payload.total_price_chf,
      // Follows the market — a CHF literal here booked NL conversions into
      // GA/Ads as Swiss francs, overstating revenue by the FX rate.
      currency: market.currency,
      source: isFocus ? "quiz_focus" : "quiz",
    })
    sendQuizCompletion(payload)
    advance({ email, marketing_opt_in })
  }

  const goBack = useCallback(() => setIndex((i) => Math.max(0, i - 1)), [])

  // Register the back action into the chrome context (focus mode) so the
  // top-left arrow can step the wizard back. `null` on step 1 → chrome
  // hides the affordance.
  useEffect(() => {
    chromeProgress?.setBack(index > 0 ? goBack : null)
    return () => chromeProgress?.setBack(null)
  }, [chromeProgress, index, goBack])

  if (current === "result") {
    // Header lives inside ResultPage now so it can animate between
    // the loading and loaded states without coordinating across two
    // components.
    return (
      <div ref={wrapperRef}>
        <ResultPage
          answers={answers}
          prices={prices}
          productIds={productIds}
          insurers={insurers}
          experts={experts}
          market={market}
          isFocus={isFocus}
        />
      </div>
    )
  }

  const interestKey = stepToInterestKey[current]
  const stepTitle = t(`steps.${current}.title`)
  const stepHint = t(`steps.${current}.hint`)
  const stepCurrent = current as StepId
  const info = stepInfo(stepCurrent, answers.gender)
  const infoSlot = info ? (
    <ProductInfoButton data={info} biomarkerGroups={[]} />
  ) : undefined

  return (
    <div ref={wrapperRef} className={isFocus ? "space-y-6" : "space-y-8"}>
      {isFocus ? (
        // Per-step kicker — always rendered so the title block stays
        // anchored at the same vertical position across screens. Steps
        // in the same category (4 blood-interest questions) share a
        // kicker, which reads as a soft section heading.
        <p className="type-label-lg text-center text-neutral-400">
          {t(`kickers.${stepCurrent}`)}
        </p>
      ) : (
        <>
          <header className="mb-10">
            <h1 className="type-display-md text-deep-blue-900">{t("title")}</h1>
            <p className="type-body-lg mt-3 text-neutral-600">
              {t("subtitle")}
            </p>
          </header>
          <ProgressIndicator current={index + 1} total={total} />
        </>
      )}

      <ClientOnly
        fallback={
          <StepStatic
            current={stepCurrent}
            answers={answers}
            prices={prices}
            insurers={insurers}
            advance={advance}
            onEmailSubmit={completeQuiz}
            stepTitle={stepTitle}
            stepHint={stepHint}
            interestKey={interestKey}
            infoSlot={infoSlot}
            isFocus={isFocus}
          />
        }
      >
        {/* safe-animate-presence: inside ClientOnly mount-gate */}
        <AnimatePresence mode="wait" initial={false}>
          <m.div
            key={stepCurrent}
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.2 }}
          >
            <StepStatic
              current={stepCurrent}
              answers={answers}
              prices={prices}
              insurers={insurers}
              advance={advance}
              onEmailSubmit={completeQuiz}
              stepTitle={stepTitle}
              stepHint={stepHint}
              interestKey={interestKey}
              infoSlot={infoSlot}
              isFocus={isFocus}
            />
          </m.div>
        </AnimatePresence>
      </ClientOnly>

      {index > 0 && !isFocus && (
        <div className="mt-8">
          <BackButton onClick={goBack} />
        </div>
      )}
    </div>
  )
}

interface StepStaticProps {
  current: StepId
  answers: Answers
  prices: PriceMap
  insurers: InsurersData
  advance: (patch: Partial<Answers>) => void
  onEmailSubmit: (email: string, marketing_opt_in: boolean) => void
  stepTitle: string
  stepHint: string
  interestKey: InterestKey | undefined
  infoSlot?: React.ReactNode
  isFocus?: boolean
}

function StepStatic({
  current,
  answers,
  prices,
  insurers,
  advance,
  onEmailSubmit,
  stepTitle,
  stepHint,
  interestKey,
  infoSlot,
  isFocus = false,
}: StepStaticProps) {
  return (
    <div>
      {isFocus ? (
        <h1 className="type-display-md text-center text-neutral-900">
          {stepTitle}
          {infoSlot && (
            <span className="ml-1.5 inline-block align-middle">{infoSlot}</span>
          )}
        </h1>
      ) : (
        <div className="flex items-start gap-2">
          <h1 className="type-headline-md flex-1 text-neutral-900">
            {stepTitle}
          </h1>
          {infoSlot && <div className="flex-shrink-0">{infoSlot}</div>}
        </div>
      )}
      <p
        className={
          isFocus
            ? "type-body-lg mt-3 text-center text-neutral-500"
            : "type-body-md mt-2 text-neutral-500"
        }
      >
        {stepHint}
      </p>
      <div className={isFocus ? "mt-10" : "mt-6"}>
        {current === "age" && (
          <AgeStep initial={answers.age} onSubmit={(age) => advance({ age })} />
        )}
        {current === "gender" && (
          <GenderStep
            onSelect={(gender) => advance({ gender })}
            isFocus={isFocus}
          />
        )}
        {interestKey && (
          <InterestStep
            onSelect={(yes) => advance({ [interestKey]: yes })}
            isFocus={isFocus}
          />
        )}
        {current === "mri" && (
          <MriStep answers={answers} onSubmit={advance} isFocus={isFocus} />
        )}
        {current === "mri_addons" && (
          <MriAddonsStep
            answers={answers}
            prices={prices}
            onSubmit={advance}
            isFocus={isFocus}
          />
        )}
        {current === "insurance" && (
          <InsuranceStep
            insurers={insurers}
            isFocus={isFocus}
            onSelect={(insurer_id, insurer_package_id) =>
              advance({ insurer_id, insurer_package_id })
            }
            onSkip={() =>
              advance({ insurer_id: "none", insurer_package_id: undefined })
            }
          />
        )}
        {current === "email" && (
          <EmailStep isFocus={isFocus} onSubmit={onEmailSubmit} />
        )}
      </div>
    </div>
  )
}
